import { useEffect, useState } from 'react';

import type { HubItem } from '../api/types';

import { fetchFileUrl, fetchHubItemHtml } from '../api/infoHubApi';
import { extractRenderableHtml } from '../utils/contentHtml';

export type HubItemContentState = 'loading' | 'ready' | 'notfound' | 'error';

export interface UseHubItemContentResult {
  /** Render-ready HTML, wrappers stripped. Empty when the item has no body. */
  html: string;
  /** Resolved picture URL. Empty when there is no picture, or it isn't readable yet. */
  pictureUrl: string;
  state: HubItemContentState;
}

/**
 * Loads the two things an admin-supplied hub item doesn't carry: its HTML body (stored
 * out-of-row in blob storage) and a readable URL for its picture (the admin row holds a
 * FileRecord id, not a URL).
 *
 * This is the *admin preview's* loader. The hub's own pages don't need it: their public reads
 * already return the body and a resolved picture URL in one call.
 *
 * Both are fetched together — the picture never fails the render on its own, so only the body
 * decides the state. An item that links out (`externalUrl`) has no body to fetch: the hub
 * sends the reader elsewhere, so this resolves immediately.
 */
export const useHubItemContent = (item: HubItem | null): UseHubItemContentResult => {
  const [html, setHtml] = useState('');
  const [pictureUrl, setPictureUrl] = useState('');
  const [state, setState] = useState<HubItemContentState>('loading');

  const id = item?.id ?? '';
  const kind = item?.kind ?? 'article';
  // Already-resolved URL wins; only an admin row needs the FileRecord lookup.
  const knownPictureUrl = item?.pictureUrl ?? '';
  const pictureFileRecordId = item?.pictureFileRecordId ?? '';
  const isExternal = Boolean(item?.externalUrl);

  useEffect(() => {
    let cancelled = false;

    setState('loading');
    setHtml('');
    setPictureUrl('');

    if (!id) {
      setState('notfound');
      return;
    }

    const load = async (): Promise<void> => {
      try {
        const [body, url] = await Promise.all([
          isExternal ? Promise.resolve('') : fetchHubItemHtml(kind, id),
          knownPictureUrl ? Promise.resolve(knownPictureUrl) : fetchFileUrl(pictureFileRecordId),
        ]);

        if (cancelled) {
          return;
        }

        if (body === null) {
          setState('notfound');
          return;
        }

        setHtml(extractRenderableHtml(body));
        setPictureUrl(url);
        setState('ready');
      } catch (error) {
        if (cancelled) {
          return;
        }
        console.error('Error loading hub item content:', error);
        setState('error');
      }
    };

    load();
    return (): void => {
      cancelled = true;
    };
  }, [id, kind, knownPictureUrl, pictureFileRecordId, isExternal]);

  return { html, pictureUrl, state };
};
